import { CacheLookupPolicy } from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    isTransientNetworkError,
    NetworkTransportError,
} from '~/backend/network-retry';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import { isEntraAuthEnabled, isBrokerAuthEnabled } from '~/lib/auth';
import { getStoredUser, SessionRenewalUnansweredError } from '~/lib/oidc';
import { handleError } from '~/utils/errors';
import { checkIsInSessionRouteWithoutRouter } from '../session/useIsInSessionRoute';

type AccessTokenOptions = {
    scopes: string[];
};

/** A bearer token and when it expires, in milliseconds since the epoch. */
export interface AccessToken {
    token: string;
    expiresOnTimestamp: number;
}

export interface TokenProvider {
    getToken: () => Promise<AccessToken | null>;
}

const NO_TOKEN: AccessToken = { token: '', expiresOnTimestamp: 0 };

/** Message of a token request that failed on the network. */
const TOKEN_NETWORK_FAILURE = 'The token request failed on the network.';

/**
 * What the user is told when Entra will not renew the sign-in silently, as
 * once its refresh token has expired: only a new sign-in recovers, and
 * reloading the page does not.
 */
export const ENTRA_SIGN_IN_EXPIRED =
    'Your sign-in has expired. Save your work, then sign out and sign in again.';

/**
 * MSAL error codes for a token request that never reached, or never heard
 * back from, the identity provider (msal-browser 4.x). Matched by code
 * rather than by class, as MSAL's error classes are not imported here.
 */
const MSAL_NETWORK_ERROR_CODES: ReadonlySet<string> = new Set([
    'no_network_connectivity',
    'network_error',
    'post_request_failed',
    'get_request_failed',
    'endpoints_resolution_error',
]);

/** True for an MSAL token request that failed on the network. */
function isMsalNetworkError(error: unknown): boolean {
    if (!(error instanceof Error) || !('errorCode' in error)) {
        return false;
    }
    const { errorCode } = error as Error & { errorCode: unknown };
    return (
        typeof errorCode === 'string' && MSAL_NETWORK_ERROR_CODES.has(errorCode)
    );
}

/**
 * Hook that provides an access-token provider for the signed-in user, from
 * whichever identity provider the application is signed in against: the
 * broker when it is configured, Azure Entra otherwise.
 *
 * @param {AccessTokenOptions} options - Options for the retrieved access token.
 *   `scopes` - An array of strings representing the scopes for which the token is requested.
 * @returns An object containing the token provider and authentication status.
 *   `tokenProvider` - The token provider for Azure services.
 *   `isAuthenticated` - Whether the user is authenticated.
 */
export const useAccessTokenProvider = ({ scopes }: AccessTokenOptions) => {
    const isInConnectorRoute = checkIsInSessionRouteWithoutRouter();
    const { isAuthenticated: isSessionAuthenticated } = useAuthSession();
    const isAuthenticated = isSessionAuthenticated || isInConnectorRoute;
    const { instance } = useMsal();
    const account = useAccount();

    const useBrokerToken = isBrokerAuthEnabled();

    const acquireToken = useCallback(async (): Promise<AccessToken> => {
        if (isInConnectorRoute) {
            return NO_TOKEN;
        }

        if (useBrokerToken) {
            try {
                const user = await getStoredUser();
                if (user === null) {
                    throw new Error(
                        'No broker session. User may need to sign in.'
                    );
                }
                return {
                    token: user.access_token,
                    // oidc-client-ts reports expiry in seconds since epoch.
                    expiresOnTimestamp: (user.expires_at ?? 0) * 1000,
                };
            } catch (error) {
                if (error instanceof SessionRenewalUnansweredError) {
                    // As for Entra below: the broker was unreachable, which
                    // says nothing about the sign-in.
                    throw new NetworkTransportError(TOKEN_NETWORK_FAILURE);
                }
                handleError(
                    error,
                    'Token acquisition failed. User may need to sign in again.'
                );
                return NO_TOKEN;
            }
        }

        if (!isEntraAuthEnabled()) {
            return NO_TOKEN;
        }

        try {
            if (!account) {
                throw new Error(
                    'No active account set. User may need to sign in.'
                );
            }

            const tokenResult = await instance.acquireTokenSilent({
                scopes,
                account,
                // A cross-origin isolated page cannot load Microsoft's page in
                // the hidden iframe MSAL renews through once the refresh token
                // has expired, so that renewal is not attempted and the user
                // is asked to sign in again without first waiting out its
                // timeout.
                cacheLookupPolicy: window.crossOriginIsolated
                    ? CacheLookupPolicy.AccessTokenAndRefreshToken
                    : CacheLookupPolicy.Default,
            });

            return {
                token: tokenResult.accessToken,
                expiresOnTimestamp: tokenResult.expiresOn?.getTime() || 0,
            };
        } catch (error) {
            if (isMsalNetworkError(error) || isTransientNetworkError(error)) {
                // Entra was unreachable (e.g. mid network switch), which says
                // nothing about the sign-in. Every caller resolves the token
                // inside a request attempt, so failing it as a transport
                // failure lets that request's retry fetch the token again; an
                // empty token would only buy a guaranteed 401.
                throw new NetworkTransportError(TOKEN_NETWORK_FAILURE);
            }
            handleError(error, ENTRA_SIGN_IN_EXPIRED);
            return NO_TOKEN;
        }
    }, [instance, scopes, isInConnectorRoute, account, useBrokerToken]);

    // getToken must always invoke the *latest* closure (current account and
    // scopes) while the tokenProvider object identity stays stable — backend
    // clients are memoized on it. A ref bridges the two; a directly captured
    // closure would permanently use the first render's account, acquiring
    // tokens for a stale identity after an account switch.
    const acquireTokenRef = useRef(acquireToken);
    useEffect(() => {
        acquireTokenRef.current = acquireToken;
    }, [acquireToken]);

    const [tokenProvider] = useState<TokenProvider>(() => ({
        async getToken(): Promise<AccessToken | null> {
            return acquireTokenRef.current();
        },
    }));

    return { tokenProvider, isAuthenticated };
};
